// ./src/components/features/authentication/ForgotPasswordForm/ForgotPasswordForm.tsx
import React, { useState } from "react";
import { Stack, Button, TextInput, Text, Anchor, Alert } from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconInfoCircle, IconCheck } from "@tabler/icons-react";

import { useAuth } from "@/hooks";

export interface ForgotPasswordFormData {
  username: string;
}

export interface ForgotPasswordFormErrors {
  username?: string;
  general?: string;
}

export interface ForgotPasswordFormProps {
  /** Callback function called when reset request is successful */
  onResetRequestSuccess?: () => void;

  /** Callback function called when user wants to navigate back to signin */
  onNavigateToSignin?: () => void;

  /** Whether to show the "Back to Sign In" link */
  showSigninLink?: boolean;
}

export const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = ({
  onResetRequestSuccess,
  onNavigateToSignin,
  showSigninLink = true,
}) => {
  const [loading, setLoading] = useState(false);
  const [serverErrors, setServerErrors] = useState<ForgotPasswordFormErrors>(
    {}
  );
  const [successMessage, setSuccessMessage] = useState<string>("");
  const { resetPasswordRequest } = useAuth({ autoCheck: false });

  const form = useForm<ForgotPasswordFormData>({
    initialValues: {
      username: "",
    },
    validate: {
      username: (value) => {
        if (!value) return "Username or email is required";
        return null;
      },
    },
  });

  const handleSubmit = async (values: ForgotPasswordFormData) => {
    setLoading(true);
    setServerErrors({});
    setSuccessMessage("");

    try {
      const success = await resetPasswordRequest({
        username: values.username,
      });

      if (success) {
        setSuccessMessage(
          "Password reset link has been sent to your email address. Please check your inbox."
        );

        // Notify parent component that request was successful
        setTimeout(() => {
          onResetRequestSuccess?.();
        }, 3000);
      } else {
        setServerErrors({
          general:
            "Failed to send password reset link. Please check your username and try again.",
        });
      }
    } catch (error) {
      console.error("Password reset request error:", error);
      setServerErrors({
        general:
          error instanceof Error
            ? error.message
            : "An error occurred while requesting password reset. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSigninLinkClick = (e: React.MouseEvent) => {
    e.preventDefault();
    onNavigateToSignin?.();
  };

  // Show success state
  if (successMessage) {
    return (
      <Alert color="green" icon={<IconCheck size={16} />}>
        {successMessage}
      </Alert>
    );
  }

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack gap="md">
        {serverErrors.general && (
          <Alert color="red" icon={<IconInfoCircle size={16} />}>
            {serverErrors.general}
          </Alert>
        )}

        <Text size="sm" c="dimmed" mb="xs">
          Enter your username or email address and we'll send you a link to
          reset your password.
        </Text>

        <TextInput
          label="Username or Email"
          placeholder="Enter your username or email"
          required
          {...form.getInputProps("username")}
          error={form.errors.username || serverErrors.username}
        />

        <Button type="submit" fullWidth size="md" mt="md" loading={loading}>
          Send Reset Link
        </Button>

        {showSigninLink && (
          <Text ta="center" size="sm" c="dimmed">
            Remember your password?{" "}
            <Anchor
              onClick={handleSigninLinkClick}
              fw={500}
              style={{ cursor: "pointer" }}
            >
              Back to Sign In
            </Anchor>
          </Text>
        )}
      </Stack>
    </form>
  );
};
